import React from "react";
import { CheckCircle2 } from "lucide-react";
import { ResponsiveDataList } from "../../../../../components/responsive";
import type { LifecycleRetreat } from "../../../../../types/retreatLifecycle";
import {
  formatLanguage,
  formatRetreatDateRange,
  statusChip,
} from "../../lifecycle/lifecycleUi";
import { SectionShell } from "../../lifecycle/SectionShell";
import { retreatTitleCell, tooltipCell } from "../../lifecycle/manageRetreatColumns";
import { ClosedRetreatCard } from "../cards/ClosedRetreatCard";
import { SectionActionButtons } from "../SectionActionButtons";
import { SECTION_IDS } from "../manageRetreatTypes";

export function ClosedSection({
  rows,
  total,
  loading,
  searchValue,
  onSearchChange,
  dataFetchApi,
  isSuperAdmin,
  onHistory,
  onDelete,
  onApprove,
  onRestore,
}: {
  rows: LifecycleRetreat[];
  total: number;
  loading: boolean;
  searchValue: string;
  onSearchChange: (v: string) => void;
  dataFetchApi: (...args: any[]) => void;
  isSuperAdmin: boolean;
  onHistory: (r: LifecycleRetreat) => void;
  onDelete: (r: LifecycleRetreat) => void;
  onApprove: (r: LifecycleRetreat) => void;
  onRestore: (r: LifecycleRetreat) => void;
}) {
  const columns = [
    {
      id: "action",
      name: "Action",
      sortable: false,
      button: true,
      grow: 0,
      cell: (r: LifecycleRetreat) => (
        <SectionActionButtons
          r={r}
          kind="completed"
          isSuperAdmin={isSuperAdmin}
          onHistory={onHistory}
          onDelete={onDelete}
          onApprove={onApprove}
          onRestore={onRestore}
        />
      ),
      width: "130px",
    },
    {
      id: "retreatId",
      name: "Retreat Id",
      sortable: true,
      grow: 0,
      selector: (r: LifecycleRetreat) => r.id,
      width: "130px",
    },
    {
      id: "title",
      name: "Retreat",
      sortable: true,
      grow: 0,
      cell: retreatTitleCell,
      width: "400px",
    },
    {
      id: "language",
      name: "Language",
      sortable: true,
      grow: 0,
      selector: (r: LifecycleRetreat) => formatLanguage(r.language),
      cell: (r: LifecycleRetreat) => tooltipCell(formatLanguage(r.language)),
      width: "150px",
    },
    {
      id: "dateFrom",
      name: "Dates",
      sortable: true,
      grow: 0,
      selector: (r: LifecycleRetreat) =>
        formatRetreatDateRange(r.dateFrom, r.dateTo),
      cell: (r: LifecycleRetreat) =>
        tooltipCell(formatRetreatDateRange(r.dateFrom, r.dateTo)),
      width: "200px",
    },
    {
      id: "lifecycleStatus",
      name: "Status",
      sortable: true,
      grow: 0,
      cell: () => tooltipCell(statusChip("Closed"), "Closed"),
      width: "150px",
    },
  ];

  return (
    <SectionShell
      id={SECTION_IDS.completed}
      title="Closed Retreats"
      subtitle="Retreats that have completed confirmation, been validated and closed, and marked completed."
      bgcolor="#eef7f0"
      icon={<CheckCircle2 size={22} color="#216b30" />}
      total={total}
      searchValue={searchValue}
      onSearchChange={onSearchChange}
    >
      <ResponsiveDataList
        data={rows}
        total={total}
        loading={loading}
        isServerSide
        sortServer
        countPerPage={10}
        dataFetchApi={dataFetchApi}
        columns={columns}
        cardGridColumns={{ xs: "1fr", md: "repeat(2, minmax(0, 1fr))" }}
        renderCard={(r) => (
          <ClosedRetreatCard
            r={r}
            isSuperAdmin={isSuperAdmin}
            onHistory={onHistory}
            onDelete={onDelete}
            onApprove={onApprove}
            onRestore={onRestore}
          />
        )}
      />
    </SectionShell>
  );
}
